import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { CirclePlus, CircleMinus } from "lucide-react";

import { createNewIntentPromptsAPI, fetchSingleIntentPromptsAPI, fetchCustomFieldsAPI } from "../../../services/intent_prompt";

import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Chip from "@mui/material/Chip";

const CreateIntentPrompt = ({ type }) => {
  let { id } = useParams();
  if (type) id = type;

  let navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [intentPrompt, setIntentPrompt] = useState(null);
  const [customFields, setCustomFields] = useState(null);
  const [customFieldsSelected, setCustomFieldsSelected] = useState([]);
  const [addFields, setAddFields] = useState(false);
  const [searchCustomFields, setSearchCustomFields] = useState("");
  const [modelsLoading, setModelsLoading] = useState(false);
  const [llmModels, setLlmModels] = useState([]);

  const { register, handleSubmit, reset, control } = useForm({
    defaultValues: {
      id: intentPrompt?.id,
      name: type ? type : "",
      llm_model: [],
      type: type ? type : "call",
      prompt_text: "",
    },
  });

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchDataFromApi = async () => {
    try {
      if (id) {
        const responseData = await fetchSingleIntentPromptsAPI(id);
        setIntentPrompt(responseData.data);
      }
    } catch (error) {
      // setError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomFieldsFromApi = async () => {
    try {
      const responseData = await fetchCustomFieldsAPI();
      setCustomFields(responseData.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchLlmModels = async () => {
    try {
      setModelsLoading(true);

      const response = await fetch("https://api.deepinfra.com/models/list");

      const data = await response.json();

      if (!response.ok) {
        throw new Error("Failed to fetch models");
      }

      const textModels = data
        .filter((model) => model.reported_type === "text-generation")
        .map((model) => ({
          id: model.model_name,
          ...model,
        }));

      setLlmModels(textModels);
    } finally {
      setModelsLoading(false);
    }
  };

  useEffect(() => {
    fetchLlmModels();
    fetchDataFromApi();
    fetchCustomFieldsFromApi();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (intentPrompt) {
      const models = intentPrompt.llm_model ? intentPrompt.llm_model.split("|").filter(Boolean) : [];
      reset({
        id: id,
        llm_model: models,
        name: type ? type : intentPrompt.name,
        type: type ? type : intentPrompt.type,
        prompt_text: intentPrompt.prompt_text,
      });
    }
    if (!type && intentPrompt?.prompt_fields) {
      setCustomFieldsSelected(intentPrompt.prompt_fields.split("|"));
    }
  }, [intentPrompt, reset, id, type]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        llm_model: data.llm_model?.length ? data.llm_model.join("|") : null,
        prompt_fields: customFieldsSelected,
      };
      const responseData = await createNewIntentPromptsAPI(payload);
      if (responseData?.status === "success") {
        setSuccess(responseData);
        if (!type) {
          return navigate(`/dashboard/intent-prompt/save/${responseData?.data?.id}`);
        }
      }
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const addOrRemoveField = (item, addremove) => {
    let previousFields = customFieldsSelected;
    previousFields = previousFields.filter((itm) => itm !== item.fieldKey);
    if (addremove === "add") {
      previousFields.push(item.fieldKey);
    }
    setCustomFieldsSelected(previousFields);
  };

  const names = {
    "faq-prompt": "for FAQ",
    "objections-prompt": "for Objections",
  };

  const allowedModels = [
    { name: "deepseek-ai", label: "Deepseek" },
    { name: "Qwen", label: "Qwen" },
    { name: "meta-llama", label: "Llama" },
    { name: "google", label: "Google" },
    { name: "nvidia", label: "Nvidia" },
    { name: "anthropic", label: "Claude" },
    { name: "microsoft", label: "Microsoft" },
    { name: "mistralai", label: "Mistralai" },
    { name: "gryphe", label: "Grype" },
    { name: "black-forest-labs", label: "Flux" },
  ];

  // Group models by category, sorted by label
  const groupedModels = () => {
    const groups = {};
    allowedModels.forEach((allowed) => {
      const categoryModels = llmModels.filter((model) => model.id.toLowerCase().startsWith(allowed.name.toLowerCase()));
      if (categoryModels.length) {
        groups[allowed.label] = categoryModels;
      }
    });
    return groups;
  };

  const centsToPerMillion = (cents) => {
    if (cents === null || cents === undefined) return null;
    const dollars = cents * 10000;
    return dollars < 0.01 ? dollars.toFixed(4) : dollars.toFixed(2);
  };

  const formatModelPricing = (pricing) => {
    if (!pricing) return null;

    // const cached = centsToPerMillion(pricing.rate_per_input_token_cached);
    const input = centsToPerMillion(pricing.cents_per_input_token);
    const output = centsToPerMillion(pricing.cents_per_output_token);

    // if (!cached && !input && !output) return null;
    if (!input && !output) return null;

    const parts = [];
    // if (cached) parts.push(`$${cached} cached`);
    if (input) parts.push(`$${input} in`);
    if (output) parts.push(`$${output} out`);

    return `${parts.join(", ")} / 1M`;
  };

  return (
    <div className="flex flex-row gap-4 px-10 pb-24">
      <div className="flex items-center justify-center w-full">
        <div className="w-full">
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex justify-end">
              <div className="flex flex-col mt-5">
                <button
                  type="submit"
                  className={`group flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 
                  ${loading ? "bg-gray-400 cursor-not-allowed" : "hover:bg-blue-700 focus:ring-blue-500"}`}
                >
                  Save Changes
                </button>
                <div className="flex justify-center">
                  {!!loading && <div className="loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-10 w-10 opacity-90 mt-4"></div>}
                  {!!error && !loading && <div className="text-center text-red-500">{error?.message}</div>}
                  {!loading && <div className="pt-2 text-green-600">{success?.message}</div>}
                </div>
              </div>
            </div>

            <div className="flex flex-row w-full gap-4">
              <div className="w-full">
                {!type && (
                  <>
                    <div className="">
                      <label htmlFor="typedropdown" className="">
                        Type
                      </label>
                      <select id="typedropdown" className="text-white block w-full px-3 py-3 my-4 border bg-gray-900 rounded-md outline-none border-gray-800 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" {...register("type")}>
                        <option value="call">Call</option>
                        <option value="sms">SMS</option>
                        <option value="auto_reply_sms">AutoReply SMS</option>
                        <option value="email">Email</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="voicemail">Voicemail</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    <div className="space-y-2 mt-6">
                      <label htmlFor="name" className="">
                        Name
                      </label>
                      <input id="name" name="name" type="text" required {...register("name")} className="relative block w-full px-3 py-3 border bg-gray-900 rounded-md outline-none border-gray-800 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm" />
                    </div>

                    <div className="space-y-2 mt-6">
                      <label htmlFor="llmmodeldropdown">LLM Model</label>
                      <FormControl fullWidth size="small" variant="outlined" className="bg-gray-900 rounded-md">
                        <Controller
                          name="llm_model"
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              multiple
                              disabled={modelsLoading}
                              renderValue={(selected) => {
                                if (!selected || selected.length === 0) {
                                  return <span className="text-gray-400">Select models</span>;
                                }
                                return (
                                  <div className="flex flex-wrap gap-1">
                                    {selected.map((value) => (
                                      <Chip
                                        key={value}
                                        label={value}
                                        size="small"
                                        sx={{
                                          backgroundColor: "#1f2937",
                                          color: "#e5e7eb",
                                          borderRadius: "4px",
                                          "& .MuiChip-deleteIcon": {
                                            color: "#9ca3af",
                                            "&:hover": { color: "#f87171" },
                                          },
                                        }}
                                      />
                                    ))}
                                  </div>
                                );
                              }}
                              MenuProps={{
                                slotProps: {
                                  paper: {
                                    style: {
                                      maxHeight: 250, // show ~5-6 items
                                      width: 350,
                                    },
                                  },
                                },
                              }}
                              sx={{
                                "& .MuiSelect-select": {
                                  padding: "8px 14px",
                                  minHeight: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: "4px",
                                  color: "#e5e7eb",
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#4b5563",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#6b7280",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#3b82f6",
                                },
                              }}
                            >
                              {modelsLoading ? (
                                <MenuItem disabled>Loading models...</MenuItem>
                              ) : (
                                // Render grouped categories with headers
                                Object.entries(groupedModels()).map(([category, models]) => [
                                  // Category header (disabled)
                                  <MenuItem
                                    key={`header-${category}`}
                                    disabled
                                    sx={{
                                      fontWeight: "bold",
                                      backgroundColor: "#1f2937",
                                      color: "#9ca3af",
                                      cursor: "default",
                                      "&:hover": { backgroundColor: "#1f2937" },
                                    }}
                                  >
                                    {category}
                                  </MenuItem>,
                                  // Model items
                                  ...models.map((model) => {
                                    const priceLabel = formatModelPricing(model.pricing);
                                    return (
                                      <MenuItem key={model.id} value={model.id} sx={{ paddingLeft: "32px" }}>
                                        <div className="flex flex-col">
                                          <span>{model.id}</span>
                                          {priceLabel && <span className="text-xs text-gray-400">{priceLabel}</span>}
                                        </div>
                                      </MenuItem>
                                    );
                                  }),
                                ])
                              )}
                            </Select>
                          )}
                        />
                      </FormControl>
                    </div>
                  </>
                )}

                <div className="space-y-2 mt-6">
                  <label htmlFor="prompt_text" className="">
                    LLM Prompt Text {!!type ? names[type] : ""}
                  </label>
                  <textarea id="prompt_text" name="prompt_text" type="text" required {...register("prompt_text")} className="relative block w-full px-3 py-3 border bg-gray-900 rounded-md outline-none border-gray-800 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm" cols={4} rows={6} />
                </div>

                {!type && !!customFields && (
                  <div className="space-y-2 mt-6">
                    <label htmlFor="" className="">
                      Response Update Fields:
                    </label>
                    <div>
                      <div className="flex justify-between items-end">
                        <div className="space-y-2 mt-6">
                          <label htmlFor="" className="">
                            Search
                          </label>
                          <input
                            type="text"
                            value={searchCustomFields}
                            onChange={(e) => {
                              setSearchCustomFields(e.target.value);
                            }}
                            className="relative block w-full px-3 py-3 border bg-gray-900 rounded-md outline-none border-gray-800 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                          />
                        </div>
                        <div className="flex flex-col">
                          <button
                            onClick={() => {
                              setAddFields(!addFields);
                            }}
                            type="button"
                            className="group flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            {!addFields ? "Show Fields" : "Hide Fields"}
                          </button>
                        </div>
                      </div>
                      {!!addFields && (
                        <div className="py-3 my-10 rounded-md bg-gray-900 border-gray-800 h-96 overflow-auto grid grid-cols-[auto_1fr]">
                          {customFields
                            .filter((field) => field.name.toLowerCase().includes(searchCustomFields.toLowerCase()))
                            .map((oneField) => (
                              <React.Fragment key={oneField.id}>
                                <div className={"px-3 py-2 " + (customFieldsSelected.indexOf(oneField.fieldKey) > -1 ? "text-gray-600 cursor-default" : "hover:bg-gray-800")}>
                                  <CirclePlus
                                    className={"inline mr-3 " + (customFieldsSelected.indexOf(oneField.fieldKey) > -1 ? "" : "cursor-pointer")}
                                    size="20"
                                    onClick={() => {
                                      addOrRemoveField(oneField, "add");
                                    }}
                                  />
                                  {oneField.name}
                                </div>
                                <div className="px-3 py-2">{oneField.fieldKey}</div>
                              </React.Fragment>
                            ))}
                        </div>
                      )}

                      <div className="grid grid-cols-[auto_1fr]">
                        {customFields.map((oneField) =>
                          customFieldsSelected.indexOf(oneField.fieldKey) > -1 ? (
                            <React.Fragment key={"sel" + oneField.id}>
                              <div className="px-3 py-2">
                                <CircleMinus
                                  className="inline mr-3"
                                  size="20"
                                  onClick={() => {
                                    addOrRemoveField(oneField, "remove");
                                  }}
                                />
                                {oneField.name}
                              </div>
                              <div className="px-5 py-2">{oneField.fieldKey}</div>
                            </React.Fragment>
                          ) : null,
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateIntentPrompt;
